import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ReplayIcon from '@mui/icons-material/Replay';
import React, { useState } from 'react';
import { PogResetMappingRunController } from '../hooks/usePogResetMappingRun';

type PogResetMappingActionsProps = {
  run: PogResetMappingRunController;
  /** "Generate mappings" only computes open tasks without a cached result — hide it when there are none. */
  showGenerate: boolean;
  disabled?: boolean;
};

export const PogResetMappingActions: React.FC<PogResetMappingActionsProps> = ({
  run,
  showGenerate,
  disabled = false,
}) => {
  const [showGeneratePanel, setShowGeneratePanel] = useState(false);
  const [showCalculatePanel, setShowCalculatePanel] = useState(false);
  const [generateSkipPr, setGenerateSkipPr] = useState(true);
  const [calculateSkipPr, setCalculateSkipPr] = useState(true);
  const [calculatePrefix, setCalculatePrefix] = useState('');

  const isBusy = run.isRunning || run.isTriggering || disabled;

  const handleGenerate = () => {
    setShowGeneratePanel(false);
    run.start(false, undefined, generateSkipPr || undefined);
  };

  const handleCalculate = () => {
    const prefix = calculatePrefix.trim().toUpperCase();
    setShowCalculatePanel(false);
    run.start(true, prefix || undefined, calculateSkipPr || undefined);
  };

  return (
    <>
      {run.isRunning && (
        <div className="flex items-center gap-2 text-sm text-blue-600">
          <span className="inline-block w-2 h-2 bg-blue-600 rounded-full animate-pulse" />
          Mapping calculation running…
        </div>
      )}
      {showGenerate && (
        <div className="relative">
          <button
            onClick={() => setShowGeneratePanel((prev) => !prev)}
            disabled={isBusy}
            title="Compute candidates for open tasks that have no cached result yet"
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 whitespace-nowrap"
          >
            <AutoFixHighIcon fontSize="small" />
            Generate mappings
          </button>
          {showGeneratePanel && (
            <div className="absolute right-0 top-full mt-2 z-20 bg-white border border-gray-200 rounded-lg shadow-lg p-4 w-72">
              <label
                className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer"
                title="Skip camera product recognition — only field-scan matches propose. Much faster on fresh stores without camera data."
              >
                <input
                  type="checkbox"
                  checked={generateSkipPr}
                  onChange={(e) => setGenerateSkipPr(e.target.checked)}
                />
                Skip PR (scans only — fast)
              </label>
              <button
                onClick={handleGenerate}
                disabled={run.isRunning || run.isTriggering}
                className="mt-3 w-full px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
              >
                Generate mappings
              </button>
            </div>
          )}
        </div>
      )}
      <div className="relative">
        <button
          onClick={() => setShowCalculatePanel((prev) => !prev)}
          disabled={isBusy}
          title="Recompute candidates — optionally restricted to a mod-tag prefix"
          className="px-4 py-2 border border-blue-600 text-blue-600 rounded-md hover:bg-blue-50 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 whitespace-nowrap"
        >
          <ReplayIcon fontSize="small" />
          Calculate
        </button>
        {showCalculatePanel && (
          <div className="absolute right-0 top-full mt-2 z-20 bg-white border border-gray-200 rounded-lg shadow-lg p-4 w-72">
            <label className="block text-xs text-gray-500 mb-1">
              Tag name prefix (optional) - restricts candidates to matching mods
            </label>
            <input
              type="text"
              value={calculatePrefix}
              onChange={(e) => setCalculatePrefix(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCalculate()}
              placeholder="XX-15-L-1"
              className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm uppercase focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <label
              className="mt-3 flex items-center gap-2 text-xs text-gray-600 cursor-pointer"
              title="Skip camera product recognition — only field-scan matches propose; cached PR candidates are kept. Much faster on fresh stores without camera data."
            >
              <input
                type="checkbox"
                checked={calculateSkipPr}
                onChange={(e) => setCalculateSkipPr(e.target.checked)}
              />
              Skip PR (scans only — fast)
            </label>
            <button
              onClick={handleCalculate}
              disabled={run.isRunning || run.isTriggering}
              className="mt-3 w-full px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
            >
              {calculatePrefix.trim()
                ? `Calculate for ${calculatePrefix.trim().toUpperCase()}*`
                : 'Calculate for all mods'}
            </button>
          </div>
        )}
      </div>
    </>
  );
};
